import { Link } from "../lib/router";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { PageHeader } from "../components/PageHeader";
import { Overline, Reveal } from "../components/motion";
import { PButton } from "../components/forms";
import { CraftSection } from "../components/CraftSection";
import { EQUIPMENT } from "../data/products";

export default function Equipment() {
  const { t, lang } = useLanguage();
  useMeta(t.seo.equipment[0], t.seo.equipment[1]);

  return (
    <div data-testid="equipment-page">
      <PageHeader id="equipment" overline={t.equipment.overline} lines={t.equipment.title} sub={t.equipment.sub} />

      <div className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12 lg:px-20">
        <div className="grid gap-x-12 gap-y-24 md:grid-cols-2">
          {EQUIPMENT.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.12} className={i % 2 ? "md:mt-24" : ""}>
              <article data-testid={`equipment-item-${p.slug}`}>
                <div className="group relative aspect-[4/5] overflow-hidden" style={{ backgroundColor: p.backdrop }}>
                  <img
                    src={p.image}
                    alt={p.name}
                    data-testid={`equipment-image-${p.slug}`}
                    className={`h-full w-full transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03] ${
                      p.cover ? "object-cover" : "object-contain p-10 drop-shadow-[0_28px_40px_rgba(0,0,0,0.18)] md:p-14"
                    }`}
                  />
                </div>
                <div className="mt-8">
                  <Overline className="mb-3">{String(i + 1).padStart(2, "0")}</Overline>
                  <h2 data-testid={`equipment-name-${p.slug}`} className="font-serif text-3xl font-light tracking-tight text-[#1C1C1A] md:text-4xl">
                    {p.name}
                  </h2>
                  <p className="mt-3 max-w-sm text-sm font-light leading-relaxed text-[#66625A]">{p.desc[lang]}</p>
                  <dl className="mt-8 space-y-3 text-sm">
                    <div className="flex justify-between gap-6 border-b border-[#1C1C1A]/10 pb-3">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#66625A]">{t.common.material}</dt>
                      <dd className="text-right text-[#1C1C1A]">{p.material[lang]}</dd>
                    </div>
                    <div className="flex justify-between gap-6 border-b border-[#1C1C1A]/10 pb-3">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#66625A]">{t.common.dims}</dt>
                      <dd data-testid={`equipment-dims-${p.slug}`} className="text-right text-[#1C1C1A]">{p.dims || t.common.onRequest}</dd>
                    </div>
                  </dl>
                  <div className="mt-8 flex flex-wrap items-center gap-6">
                    <span data-testid={`equipment-price-${p.slug}`} className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t.common.priceOnRequest}</span>
                    <PButton to={`/anfrage?produkt=${p.slug}`} variant="outline" data-testid={`equipment-quote-${p.slug}`}>
                      {t.equipment.enquire}
                    </PButton>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <section className="border-y border-[#1C1C1A]/10 bg-[#F3EFEA]">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-6 py-16 md:px-12 lg:flex-row lg:items-center lg:justify-between lg:px-20">
          <div>
            <Overline className="mb-3">Beratung</Overline>
            <p className="max-w-2xl font-serif text-2xl font-light leading-snug text-[#1C1C1A] md:text-3xl">
              Equipment für dein Studio oder deinen Private Space zusammenstellen.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <PButton to="/anfrage" variant="solid">Angebot anfragen</PButton>
            <Link to="/kontakt" className="inline-flex items-center border-b border-[#1C1C1A]/30 font-mono text-[10px] uppercase tracking-[0.25em] text-[#1C1C1A] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]">Persönlich beraten lassen</Link>
          </div>
        </div>
      </section>

      <CraftSection />
    </div>
  );
}
